/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from 'axios';
import ResetPasswordRequestModal from 'components/Auth/ResetPasswordRequestModal/ResetPasswordRequestModal';
import Button from 'components/UI_kit_3/Button/Button';
import RouterLinkButton from 'components/UI_kit_3/Button/RouterLinkButton';
import FormText from 'components/UI_kit_3/FormText/FormText';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useAppDispatch, useUserThunkDispatch } from 'hooks';
import useAsyncFetch from 'hooks/async-fetch';
import React from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IResponse } from 'services/api/axios';
import { userActions } from 'store/user';
import { User } from 'store/user/types';
import { ILoginBody } from 'store/user/types/api-bodies.types';
import { ILoginForm } from 'store/user/types/forms.types';
import { UserStatus } from 'store/user/types/state.types';
import * as yup from 'yup';

export const AUTH_ERROR_CODES = {
  MAINTENANCE: 618,
  EXPIRED_PASSWORD: 656,
  NEW_PASSWORD_NEEDED: 658,
  TWO_FA_REQUIRED: 657,
  BLOCKED: 601,
};

const LoginForm: React.FC = () => {
  const thunkUserDispatch = useUserThunkDispatch();
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [blockedError, setBlockedError] = React.useState<[string, string] | null>(null);

  const schema = React.useMemo(() => yup.object().shape({
    email: yup.string().email(t('form.error.wrong_value')).required(t('form.error.required')).lowercase(),
    password: yup.string().required(t('form.error.required')),
  }), [t]);

  const [handleLogin, loading] = useAsyncFetch({
    asyncFunc: async (form: ILoginForm) => {
      const body: ILoginBody = {
        login: form.email,
        password: form.password,
      };
      setBlockedError(null);
      const res = await thunkUserDispatch(userActions.signIn(body, i18n.language));
      return res;
    },
    errorHandler: ({ error, errMessage, params }) => {
      if (axios.isAxiosError(error)) {
        const errors = error.response?.data?.errors as IResponse['errors'];
        const maintenanceError = errors?.find((_error) => _error.code === AUTH_ERROR_CODES.MAINTENANCE);

        const expiredPasswordError = errors?.find((_error) => _error.code === AUTH_ERROR_CODES.EXPIRED_PASSWORD);
        const newPasswordError = errors?.find((_error) => _error.code === AUTH_ERROR_CODES.NEW_PASSWORD_NEEDED);

        const twoFaError = errors?.find((_error) => _error.code === AUTH_ERROR_CODES.TWO_FA_REQUIRED);
        const endDate = maintenanceError?.message.split(' ').pop() || null;

        const userBlockedError = errors?.find((_error) => _error.code === AUTH_ERROR_CODES.BLOCKED);

        if (maintenanceError) {
          const user = new User();
          user.status = UserStatus.Maintenance;
          user.maintenanceEndDate = endDate;
          dispatch(userActions.setUser(user));
          dispatch(userActions.setLoading(false));
          navigate('/maintenance', { replace: true });
          return;
        }

        if (expiredPasswordError) {
          const user = new User();
          user.status = UserStatus.New;
          user.state = 'expired';
          user.tempLogin = params[0].email;
          user.tempPass = params[0].password;
          dispatch(userActions.setUser(user));
          dispatch(userActions.setLoading(false));
          return;
        }

        if (newPasswordError) {
          const user = new User();
          user.status = UserStatus.New;
          user.state = 'new';
          user.tempLogin = params[0].email;
          user.tempPass = params[0].password;
          dispatch(userActions.setUser(user));
          dispatch(userActions.setLoading(false));
          return;
        }

        if (twoFaError) {
          const user = new User();
          user.status = UserStatus.New;
          user.state = '2fa';
          user.tempLogin = params[0].email;
          user.tempPass = params[0].password;
          dispatch(userActions.setUser(user));
          dispatch(userActions.setLoading(false));
          return;
        }

        if (userBlockedError) {
          let [blockedMessage, blockedDate] = userBlockedError?.message ? userBlockedError.message.split(': ') : [];
          blockedMessage = `${blockedMessage}:`;
          blockedDate = dayjs(blockedDate).format('DD.MM.YYYY HH:mm');
          setBlockedError([blockedMessage, blockedDate]);
          dispatch(userActions.setLoading(false));
          return;
        }

        setFieldError('password', errMessage);
        return;
      }
      setFieldError('password', errMessage);
    },
    disableRedirectOn401: true,
  });

  const {
    handleSubmit,
    handleChange,
    values,
    touched,
    errors,
    setFieldError,
  } = useFormik({
    validationSchema: schema,
    onSubmit: handleLogin,
    initialValues: {
      email: '',
      password: '',
    },
    validateOnBlur: true,
    validateOnChange: false,
  });

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <FormText
        className="w-100 mb-2xl"
        id="email"
        type="text"
        label={t('form.field.email')}
        name="email"
        value={values.email}
        onChange={handleChange}
        error={errors.email}
        isInvalid={touched.email && !!errors.email}
        qa-element="auth-email"
        qaElementFeedback="auth-email-error"
      />
      <FormText
        className="w-100 mb-3xl"
        id="password"
        type="password"
        label={t('form.field.password')}
        name="password"
        autoComplete="on"
        value={values.password}
        onChange={handleChange}
        error={errors.password}
        isInvalid={touched.password && !!errors.password}
        qa-element="auth-password"
        qaElementFeedback="auth-password-error"
      />
      {blockedError && (
      <>
        <Form.Text className="invalid-feedback d-block" qa-element="error-block-0">{blockedError[0]}</Form.Text>
        <Form.Text className="invalid-feedback d-block mt-0" qa-element="error-block-1">{blockedError[1]}</Form.Text>
      </>
      )}
      <div className="d-flex justify-content-between align-items-center mb-3xl">
        <ResetPasswordRequestModal initialEmail={values.email} />
        <RouterLinkButton variant="link" to="/registration" qa-element="to-registration">
          {t('auth.signup')}
        </RouterLinkButton>
      </div>
      <Button className="w-100" size="lg" disabled={loading} type="submit" variant="primary" qa-element="login">{t('form.action.login')}</Button>
    </Form>
  );
};
export default LoginForm;
